import React from "react"
import { Button, Icon, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { OpportunitiesEmptyState } from "../../page/OpportunitiesEmptyState/OpportunitiesEmptyState.js"
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import type { OpportunitySet } from "../../../types/opportunities.types"
import { OPPORTUNITY_DRAG_GRIP_FOCUS_CLASS, OPPORTUNITY_DRAG_REORDER_GRID_CLASS } from "../../../lib/opportunity-drag-reorder-grid-classes"
import {
  getSkeletonCount,
  mergeNewSetIdsIntoStoredOrder,
  orderOpportunitySets,
  readStoredOpportunitySetOrder,
  writeStoredOpportunitySetOrder,
} from "../../../lib/opportunity-set-order"
import { OpportunitySetCard, OpportunitySetCardSkeleton } from "../OpportunitySetCard/OpportunitySetCard"

export { getSkeletonCount } from "../../../lib/opportunity-set-order"

export interface OpportunitySetListProps {
  sets: OpportunitySet[]
  isLoading?: boolean
  onSelectSet: (set: OpportunitySet) => void
  /** Used only when `sets.length === 0` (e.g. Storybook); the app route uses `OpportunitiesEmptyState` instead. */
  onCreateSet?: () => void
  canCreateSet?: boolean
  onRenameSet?: (set: OpportunitySet) => void
  canRenameSet?: boolean
  onDeleteSet?: (set: OpportunitySet) => void
  canDeleteSet?: boolean
  generatingSetIds?: Set<string>
  justCompletedSetIds?: Set<string>
  errorSetIds?: Set<string>
  /** Map of setId → backend error message; passed through to the card so FAILED cards show the real reason. */
  generationErrorBySetId?: Map<string, string>
  /** Map of industry `index` → human `title` (e.g. "ingrown_hair" → "Ingrown Hair"). Cards render the label, never the raw index. */
  industryLabelByIndex?: Map<string, string>
  onRetrySet?: (set: OpportunitySet) => void
  onRefreshSet?: (set: OpportunitySet) => void
  canRefreshSet?: boolean
  /** Map of setId → unix ms when generation started; each card renders its own live elapsed clock. */
  generationStartedAt?: Map<string, number>
  /** Set IDs currently being deleted — card animates out while the mutation is in-flight. */
  deletingSetIds?: Set<string>
}

/** Mount animation duration in ms — keep in sync with `motion-safe:duration-300` on the card. */
const MOUNT_ANIM_MS = 300
/** Extra grace on top of delay+duration so the animation always finishes before the class flips off. */
const MOUNT_ANIM_BUFFER_MS = 100

function SortableSetCard({
  id,
  children,
  animationDelay,
  isDeleting,
}: {
  id: string
  children: React.ReactNode
  animationDelay: string
  isDeleting?: boolean
}) {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  // Kept as state (not a ref) so a parent re-render during the entrance window
  // doesn't strip the `animate-in` class mid-animation — that caused new cards
  // to pop into place instead of sliding in when the post-create refetch landed
  // before the 300ms animation finished.
  const [isEntering, setIsEntering] = React.useState(true)
  React.useEffect(() => {
    const delayMs = parseInt(animationDelay, 10) || 0
    const timerId = window.setTimeout(() => setIsEntering(false), delayMs + MOUNT_ANIM_MS + MOUNT_ANIM_BUFFER_MS)
    return () => window.clearTimeout(timerId)
  }, [animationDelay])

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isEntering ? { animationDelay } : {}),
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 10 : undefined,
  }

  // Build safe listeners that stopPropagation so dragging doesn't trigger card click
  const safeListeners = React.useMemo(() => {
    if (!listeners) return {}
    const wrapped: Record<string, (e: React.SyntheticEvent) => void> = {}
    for (const [key, handler] of Object.entries(listeners)) {
      wrapped[key] = (e: React.SyntheticEvent) => {
        e.stopPropagation()
        ;(handler as (e: React.SyntheticEvent) => void)(e)
      }
    }
    return wrapped
  }, [listeners])

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative h-full min-w-0 overflow-x-visible transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
        "group/sortable",
        isDeleting && "pointer-events-none scale-95 opacity-0",
        isEntering &&
          !isDeleting &&
          "motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-2 motion-safe:fill-mode-both motion-safe:duration-300 motion-safe:ease-out motion-reduce:animate-none",
      )}
    >
      {/* Drag handle — gutter left of card, vertically centered on left border */}
      <button
        type="button"
        aria-label={t("common.dragToReorder")}
        className={cn(
          "absolute -left-3 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-md",
          "cursor-grab touch-none border border-transparent text-muted-foreground/50 opacity-0 active:cursor-grabbing",
          "transition-[opacity,background-color,border-color,box-shadow,color] duration-150 motion-reduce:transition-none",
          OPPORTUNITY_DRAG_GRIP_FOCUS_CLASS,
          /* 1 — card hover: quiet chip */
          "group-hover/sortable:border-border/35 group-hover/sortable:bg-muted/16 group-hover/sortable:text-muted-foreground/50 group-hover/sortable:shadow-none group-hover/sortable:opacity-[0.78]",
          /* 2 — grip hover: clearer, slightly subdued */
          "hover:border-border/55 hover:bg-muted/36 hover:text-muted-foreground hover:shadow-sm hover:opacity-95",
        )}
        {...attributes}
        {...safeListeners}
        onClick={(e) => e.stopPropagation()}
      >
        <Icon name="grip-vertical" size="sm" color="inherit" />
      </button>
      {children}
    </div>
  )
}

export function OpportunitySetList({
  sets, isLoading, onSelectSet,
  onCreateSet, canCreateSet = true,
  onRenameSet, canRenameSet = true,
  onDeleteSet, canDeleteSet = true,
  generatingSetIds,
  justCompletedSetIds,
  errorSetIds,
  generationErrorBySetId,
  industryLabelByIndex,
  onRetrySet,
  onRefreshSet,
  canRefreshSet = true,
  generationStartedAt,
  deletingSetIds,
}: OpportunitySetListProps) {
  const { t } = useTranslation()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const [setOrder, setSetOrder] = React.useState<string[]>(() => readStoredOpportunitySetOrder())

  React.useEffect(() => {
    setSetOrder((prev) => {
      const next = mergeNewSetIdsIntoStoredOrder(sets, prev)
      if (next.length === prev.length && next.every((id, i) => id === prev[i])) return prev
      writeStoredOpportunitySetOrder(next)
      return next
    })
  }, [sets])

  const orderedSets = React.useMemo(
    () => orderOpportunitySets(sets, setOrder),
    [sets, setOrder],
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const ids = orderedSets.map((s) => s.id)
    const oldIndex = ids.indexOf(active.id as string)
    const newIndex = ids.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(ids, oldIndex, newIndex)
    setSetOrder(reordered)
    writeStoredOpportunitySetOrder(reordered)
  }

  const setsHeadingId = "opportunity-sets-heading"

  if (isLoading) {
    return (
      <section className="flex min-w-0 flex-col gap-5 overflow-x-visible pt-6" aria-labelledby={setsHeadingId}>
        <h2
          id={setsHeadingId}
          className="animate-in fade-in slide-in-from-bottom-1 fill-mode-both text-xl font-semibold leading-tight tracking-tight text-balance text-foreground duration-200 motion-reduce:animate-none"
        >
          {t("opportunities.yourSets")}
        </h2>
        <div className={OPPORTUNITY_DRAG_REORDER_GRID_CLASS}>
          {Array.from({ length: getSkeletonCount() }).map((_, i) => (
            <div
              key={i}
              className="h-full motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-2 motion-safe:fill-mode-both motion-safe:duration-300 motion-safe:ease-out motion-reduce:animate-none"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <OpportunitySetCardSkeleton />
            </div>
          ))}
        </div>
      </section>
    )
  }

  if (sets.length === 0) {
    return <OpportunitiesEmptyState onCreateSet={onCreateSet} canCreateSet={canCreateSet} />
  }

  return (
    <section className="flex min-w-0 flex-col gap-5 overflow-x-visible pt-6" aria-labelledby={setsHeadingId}>
      <h2
        id={setsHeadingId}
        className="animate-in fade-in slide-in-from-bottom-1 fill-mode-both min-w-0 text-xl font-semibold leading-tight tracking-tight text-balance text-foreground duration-200 motion-reduce:animate-none"
      >
        {t("opportunities.yourSets")}
      </h2>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedSets.map((s) => s.id)} strategy={rectSortingStrategy}>
          <div className={OPPORTUNITY_DRAG_REORDER_GRID_CLASS}>
            {orderedSets.map((set, index) => (
              <SortableSetCard
                key={set.id}
                id={set.id}
                animationDelay={`${Math.min(index, 18) * 50}ms`}
                isDeleting={deletingSetIds?.has(set.id)}
              >
                <OpportunitySetCard
                  set={set}
                  onClick={() => onSelectSet(set)}
                  onRename={() => onRenameSet?.(set)}
                  canRename={canRenameSet}
                  onDelete={onDeleteSet ? () => onDeleteSet(set) : undefined}
                  canDelete={canDeleteSet}
                  isGenerating={generatingSetIds?.has(set.id)}
                  isJustCompleted={justCompletedSetIds?.has(set.id)}
                  isError={errorSetIds?.has(set.id)}
                  errorMessage={generationErrorBySetId?.get(set.id)}
                  industryLabel={set.industry ? industryLabelByIndex?.get(set.industry) ?? set.industry : null}
                  benchmarkIndustryLabel={set.benchmarkIndustry ? industryLabelByIndex?.get(set.benchmarkIndustry) ?? set.benchmarkIndustry : null}
                  onRetry={onRetrySet}
                  onRefresh={onRefreshSet}
                  canRefresh={canRefreshSet}
                  generationStartedAt={generationStartedAt?.get(set.id)}
                />
              </SortableSetCard>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </section>
  )
}
